import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import {
  CalibrationGate,
  HomeRedirect,
  ProtectedLayout,
  RequireAuth,
  RequireUnlocked,
  RoleGuard,
  FullPageLoading,
} from "@/routes/guards";
import {
  ADMIN_ROLES,
  CLINICIAN_ROLES,
  ORG_ADMIN_ONLY_ROLES,
  SUPER_ADMIN_ROLES,
} from "@/routes/route-access";
import { SurfaceGuard } from "@/routes/surface-guard";
import { ForbiddenPage } from "@/routes/forbidden";
import { SignInScreen } from "@/routes/sign-in/SignInScreen";
import { LegacySignUpScreen } from "@/routes/legacy-signup/LegacySignUpScreen";
import { BeginCalibrationScreen } from "@/routes/begin-calibration/BeginCalibrationScreen";
import { ForcedPasswordChangeScreen } from "@/routes/forced-password-change/ForcedPasswordChangeScreen";
import { WorkspaceHomeScreen } from "@/routes/workspace/WorkspaceHomeScreen";
import { EvaluationsListScreen } from "@/routes/evaluations/EvaluationsListScreen";
import { ResultsLibraryScreen } from "@/routes/results/ResultsLibraryScreen";
import { ClientsListScreen } from "@/routes/clients/ClientsListScreen";
import { ClientIntakeScreen } from "@/routes/clients/ClientIntakeScreen";
import { ResourcesScreen } from "@/routes/resources/ResourcesScreen";
import { ProfileScreen } from "@/routes/profile/ProfileScreen";
import { CalibrationOverviewScreen } from "@/routes/calibration/CalibrationOverviewScreen";
import { CalibrationCasesScreen } from "@/routes/calibration/CalibrationCasesScreen";
import { CalibrationCaseLogScreen } from "@/routes/calibration/CalibrationCaseLogScreen";
import { CalibrationCaseScoringScreen } from "@/routes/calibration/CalibrationCaseScoringScreen";
import { CalibrationCompleteScreen } from "@/routes/calibration/CalibrationCompleteScreen";
import { AdminConsoleScreen } from "@/routes/admin/AdminConsoleScreen";
import { LegacyCertQueueScreen } from "@/routes/admin/LegacyCertQueueScreen";
import { AuditLogScreen } from "@/routes/admin/AuditLogScreen";
import { AdminUsersListScreen } from "@/routes/admin/AdminUsersListScreen";
import { AdminUserDetailScreen } from "@/routes/admin/AdminUserDetailScreen";
import { AdminCreateUserScreen } from "@/routes/admin/AdminCreateUserScreen";
import { CreateOrganizationScreen } from "@/routes/admin/CreateOrganizationScreen";
import { OrgDirectoryScreen } from "@/routes/admin/OrgDirectoryScreen";
import { OrgDetailScreen } from "@/routes/admin/OrgDetailScreen";
import { OrgAdminDashboardScreen } from "@/routes/org-admin/OrgAdminDashboardScreen";
import { AddPersonScreen } from "@/routes/org-admin/AddPersonScreen";
import { OrgAdminSettingsScreen } from "@/routes/org-admin/OrgAdminSettingsScreen";
import { SeatsAllocationScreen } from "@/routes/org-admin/SeatsAllocationScreen";
import { CatchAllPlaceholder } from "@/routes/placeholders";

// Story 6.7 — resume an interrupted evaluation. The REAL per-evaluation stepper
// target (/evaluations/:id): fetches the server-side draft, rehydrates the shared
// draft store, and opens the dedicated step-rail shell (Screens 04–08). Lazy-loaded
// like the mockup; ships its own evaluation chrome (not the global AppShell nav).
const ResumeEvaluationRoute = lazy(() => import("@/routes/evaluation/ResumeEvaluationRoute"));

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes — reachable while unauthenticated (no shell). */}
      <Route path="/sign-in" element={<SignInScreen />} />
      {/* gh-755 — /register used to render a second sign-up form with no legal
          acknowledgement checkboxes, posting to POST /auth/register, which minted
          webapp_legacy accounts with no consent record. The endpoint is now gated;
          the form is retired rather than rebuilt, because a second consent screen
          would need working links to the three CITS PDFs (blocked on #754) for an
          outcome identical to the screen below. Nothing in the app ever linked
          here, so this redirect exists for a bookmark or a typed URL — and it is a
          redirect rather than a 404 so that path lands on the real form. */}
      <Route path="/register" element={<Navigate to="/sign-up/legacy" replace />} />
      {/* Story 16.1 — legacy clinician sign-up (Screen 00). The sign-in screen's
          "Sign up as a legacy user" CTA routes here. */}
      <Route path="/sign-up/legacy" element={<LegacySignUpScreen />} />
      {/* Story 10.10 — Begin-Calibration landing (Screen 37, FR38). The LearnWorlds
          Module-12 button links here with ?email= as an untrusted prefill; no session
          is ever established from this page. */}
      <Route path="/begin-calibration" element={<BeginCalibrationScreen />} />

      {/* Authenticated but standalone (no shell, no force-change redirect → no loop). */}
      <Route element={<RequireAuth />}>
        <Route path="/forced-password-change" element={<ForcedPasswordChangeScreen />} />
      </Route>

      {/* Authenticated + in-shell. ProtectedLayout enforces auth + force-change. */}
      <Route element={<ProtectedLayout />}>
        <Route element={<AppShell />}>
          <Route index element={<HomeRedirect />} />

          {/* Clinician workspace group — CLINICIAN_ROLES only (route-access.ts);
              admins have no nav entry here and any other role → /403.
              Story 16.2 — RequireUnlocked additionally pins a gated legacy
              clinician (account disabled, cert pending) to /profile: these are
              the locked surfaces (AC7). Profile + Resources stay OUTSIDE it. */}
          <Route element={<RoleGuard allow={CLINICIAN_ROLES} />}>
            <Route element={<RequireUnlocked />}>
              {/* Story 8.1 — first-login calibration routing (FR38). An
                  uncalibrated clinician is bounced from the workspace group to
                  /calibration/overview; the already_certified claim bypasses
                  synchronously. Evaluated AFTER the 16.1 force-change and 16.2
                  legacy locks (M4 gate ordering) — hence inside RequireUnlocked. */}
              <Route element={<CalibrationGate />}>
                {/* Story 6.9 — real Workspace home (Screen 02) + Evaluations list
                    (Screen 12), both in-shell (global nav). The "New evaluation" create
                    flow is surface-gated at its CTA (the clients-list chip), so the list
                    route itself is NOT wrapped in SurfaceGuard. */}
                <Route path="/workspace" element={<WorkspaceHomeScreen />} />
                <Route path="/clients" element={<ClientsListScreen />} />
                <Route path="/clients/new" element={<ClientIntakeScreen />} />
                <Route path="/evaluations" element={<EvaluationsListScreen />} />
                {/* Story 9.13 — Results library (Screen 13): the searchable list of
                    the clinician's completed results, opening each row into the Results
                    panel (9.7) at /evaluations/{evaluation_id}. */}
                <Route path="/results" element={<ResultsLibraryScreen />} />
              </Route>

              {/* Story 8.1 — the gate's destination. Deliberately OUTSIDE
                  CalibrationGate (it must render for the uncalibrated user) but
                  inside RequireUnlocked (a gated legacy clinician still pins to
                  /profile first). Story 8.7 — the real Screen 15 overview +
                  Screen 16 case-list live HERE, beside overview (same group,
                  OUTSIDE the gate — the calibrating clinician must reach them).
                  Story 8.8 — the real :caseId/log read-only case log (Screen 18)
                  and /calibration/complete (Screen 19; self-guards on
                  state === "complete", checkpoint D5 — a router gate here would
                  loop the still-calibrating clinician). Story 8.10 — the real
                  :caseId scoring workspace (Screen 17 / 6.5-reuse per FR41)
                  replaces the placeholder and closes
                  seam-87-calibration-scoring-view; the calibration flow is now
                  completable end-to-end in the UI. */}
              <Route path="/calibration/overview" element={<CalibrationOverviewScreen />} />
              <Route path="/calibration/cases" element={<CalibrationCasesScreen />} />
              <Route
                path="/calibration/cases/:caseId"
                element={<CalibrationCaseScoringScreen />}
              />
              <Route
                path="/calibration/cases/:caseId/log"
                element={<CalibrationCaseLogScreen />}
              />
              <Route path="/calibration/complete" element={<CalibrationCompleteScreen />} />
            </Route>
          </Route>

          {/* Shared by every authenticated role (no nav-level role split). The
              gated legacy clinician's home surface — NOT wrapped in RequireUnlocked.
              Story 16.3 — the real Profile screen (hosts the Legacy AMPS certificate
              card for legacy clinicians; the full Screen 14 profile is Epic 2). */}
          <Route path="/profile" element={<ProfileScreen />} />

          {/* Resources — CLINICIAN_ROLES only (route-access.ts); admins → /403.
              Gating the route (not just hiding the nav item) closes the URL-typing gap. */}
          <Route element={<RoleGuard allow={CLINICIAN_ROLES} />}>
            <Route path="/resources" element={<ResourcesScreen />} />
          </Route>

          {/* Admin group — ADMIN_ROLES only (route-access.ts); others → /403. */}
          <Route element={<RoleGuard allow={ADMIN_ROLES} />}>
            {/* Stories 11.8 + 11.9 — Screens 20 / 22 / 21. All three sit in the
                ADMIN_ROLES block: `route-access.ts:42`'s `/admin` prefix already
                governs every child, so no rule is added. The narrower grants are
                made INSIDE the screens, where they can explain themselves — an
                org_admin reaching /admin/users/new gets a refusal that names their
                own invite path, which a RoleGuard's bare /403 could not do. */}
            <Route path="/admin/users" element={<AdminUsersListScreen />} />
            <Route path="/admin/users/new" element={<AdminCreateUserScreen />} />
            <Route path="/admin/users/:userId" element={<AdminUserDetailScreen />} />
          </Route>

          {/* Story 16.4 — legacy-cert verification queue, super_admin ONLY (org_admin
              → /403). A separate, narrower RoleGuard than the ADMIN_ROLES group above:
              only a super_admin may verify legacy AMPS certificates.
              Story 15.7 — the Screen 10 console now EXISTS, and the queue STAYS a
              dedicated route rather than folding into it. The console is a
              read-only summary with no filters (that is its W3 contract); the
              queue is a work surface with per-certificate approve/reject actions
              and its own error posture. Folding a queue into a dashboard would
              make the console the second implementation of it — the very thing
              D2 refused for the user-oversight table. The console links here
              through the nav instead. */}
          <Route element={<RoleGuard allow={SUPER_ADMIN_ROLES} />}>
            {/* Story 15.7 — the operational console (Screen 10). Its OWN path
                segment: `route-access.ts` explains why it must never mount at the
                bare /admin (canRoleAccessPath resolves with .every(), so such a
                rule would lock org_admin out of /admin/users). */}
            <Route path="/admin/console" element={<AdminConsoleScreen />} />
            <Route path="/admin/legacy-certs" element={<LegacyCertQueueScreen />} />
            {/* Story 11.7 — the audit console. In THIS block, not the ADMIN_ROLES
                one above: the backend MATRIX denies org_admin query_audit_log and
                the endpoint refuses with ADMIN-005 regardless of ?org_id=. */}
            <Route path="/admin/audit" element={<AuditLogScreen />} />
            {/* Stories 15.1 + 15.6 — the organization directory (Screen 23), its
                detail page (Screen 24) and the provisioning form (Screen 33). In
                THIS block, not the ADMIN_ROLES one above:
                Capability.manage_organizations is ANY_ORG (super_admin only), so
                an org_admin placed here would fill the form and collect a 403.
                15.6 replaced 15.1's detail placeholder with the real screen and
                added the bare /admin/orgs list — which is what makes a lost 201
                on the provisioning form recoverable: search the CRM key here.
                /admin/orgs/new stays ahead of /admin/orgs/:orgId so the literal
                segment keeps priority over the param. */}
            <Route path="/admin/orgs" element={<OrgDirectoryScreen />} />
            <Route path="/admin/orgs/new" element={<CreateOrganizationScreen />} />
            <Route path="/admin/orgs/:orgId" element={<OrgDetailScreen />} />
          </Route>

          {/* Story 14.1 — org_admin dashboard home (Screen 25), org_admin ONLY:
              the backend MATRIX denies super_admin (the org comes from the actor's
              own claim), so the guard mirrors it — a super_admin link here would
              403 (Franklin#428 posture). */}
          <Route element={<RoleGuard allow={ORG_ADMIN_ONLY_ROLES} />}>
            <Route path="/org-admin/dashboard" element={<OrgAdminDashboardScreen />} />
            {/* Story 14.2 — org_admin "Add person" invitation form (Screen 34,
                FR75). Same org_admin-only guard as the dashboard; the dashboard's
                "Add person" CTA routes here and a successful invite navigates back. */}
            <Route path="/org-admin/add-person" element={<AddPersonScreen />} />
            {/* Story 14.11 — Seats & allocation (Screen 26, FR76/FR83). Reads
                the same dashboard payload; the /org-admin prefix rule in
                route-access.ts already covers it. */}
            <Route path="/org-admin/seats" element={<SeatsAllocationScreen />} />
            {/* Story 14.9 — org_admin Settings (FR84): password + assigned email
                only. Reached from the account menu, NOT the sidebar; the
                /org-admin prefix rule in route-access.ts already covers it. */}
            <Route path="/org-admin/settings" element={<OrgAdminSettingsScreen />} />
          </Route>

          <Route path="/403" element={<ForbiddenPage />} />
        </Route>

        {/* Per-evaluation stepper (Screens 04–08/36). The /evaluations LIST is the
            in-shell Screen 12 above; /evaluations/:id is the stepper opened from
            "New evaluation" / "Continue draft", rendered shell-less (it ships its own
            left step-rail + topbar, NOT the global AppShell nav). Clinician-gated to
            mirror the route-access matrix (admins → /403); Story 16.2 RequireUnlocked
            redirects a gated legacy clinician to /profile.

            SurfaceGuard (gh-469 follow-up): the RESUME path is surface-gated too. Both
            tester paths — "Continue Evaluation" (Workspace) and "Continue" (Evaluations
            list) — land here, and a resumed session on a non-PWA iOS surface writes
            scores into the Safari/bookmark IndexedDB bucket (separate, evictable, and
            invisible to the installed PWA) — the same R-E7-3 data-loss hole the create
            gate closes. So this route shows the Install Gate on safari-proper /
            chrome-ios / home-screen-bookmark and passes through unchanged on pwa-proper
            and `other` (desktop/Android). The guard shares one allow-rule with the
            create flow (lib/surface-override.ts) so the two gated call-sites can't
            diverge. Installing/opening the PWA re-classifies to pwa-proper and clears it. */}
        <Route element={<RoleGuard allow={CLINICIAN_ROLES} />}>
          <Route element={<RequireUnlocked />}>
            <Route element={<SurfaceGuard />}>
              {/* Story 8.1 — calibration gate LAST in the chain (M4 ordering:
                  16.1 force-change → 16.2 legacy lock → E7 install gate →
                  calibration), so an uncalibrated clinician deep-linking a
                  stepper URL is routed to calibration, not into live scoring. */}
              <Route element={<CalibrationGate />}>
                <Route
                  path="/evaluations/:id"
                  element={
                    <Suspense fallback={<FullPageLoading />}>
                      <ResumeEvaluationRoute />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<CatchAllPlaceholder />} />
    </Routes>
  );
}
